import { useEffect, useRef, useCallback } from 'react';
import { smartFlowService, type FlowOpportunity } from '../services/smartFlowService';
import { readStablePrimarySessionFirst } from '../utils/stableTabStorage';
import {
  formatFlowLiquidityNotification,
  itemMatchesBigFlowFloor,
  itemMatchesFlowAlertRules,
} from '../utils/flowLiquidityAlertUtils';
import {
  loadFlowAlertSettings,
  type FlowAlertSettings,
} from '../utils/flowAlertSettingsStore';

const SEEN_KEY = 'mmec_flow_alert_seen_ids';
const MAX_SEEN = 400;

function loadConfig(): FlowAlertSettings {
  return loadFlowAlertSettings();
}

function readSeenIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.map(String));
  } catch {
    /* ignore */
  }
  return new Set();
}

function writeSeenIds(ids: Set<string>) {
  const arr = Array.from(ids).slice(-MAX_SEEN);
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

function rowDedupeKey(item: FlowOpportunity): string {
  return String(
    item.id ??
      `${item.contract_ticker}|${item.created_at}|${item.premium_size}|${item.volume}`,
  );
}

function playSound(file: string, volumePct: number) {
  try {
    const audio = new Audio(`/${file}`);
    audio.volume = Math.min(1, Math.max(0, volumePct / 100));
    void audio.play();
  } catch {
    /* ignore */
  }
}

function pushBrowserNotification(title: string, body: string) {
  if (!('Notification' in window)) return;
  const fire = () => {
    try {
      new Notification(title, { body, icon: '/logo.png', tag: title });
    } catch {
      /* ignore */
    }
  };
  if (Notification.permission === 'granted') {
    fire();
  } else if (Notification.permission !== 'denied') {
    void Notification.requestPermission().then((p) => {
      if (p === 'granted') fire();
    });
  }
}

function matchesConfig(
  item: FlowOpportunity,
  config: FlowAlertSettings,
): boolean {
  const wl = (config.watchlist_symbols || [])
    .map((s) => String(s || '').trim().toUpperCase())
    .filter(Boolean);
  if (config.watchlistOnlyAlerts && wl.length > 0) {
    const sym = String(item.symbol || '').trim().toUpperCase();
    if (!sym || !wl.includes(sym)) return false;
  }
  const enabledRules = config.rules.filter((r) => r.enabled);
  if (enabledRules.length > 0) {
    return itemMatchesFlowAlertRules(item, enabledRules, config.custom_fields);
  }
  if (config.useBigFlowFloor) {
    return itemMatchesBigFlowFloor(
      item,
      config.minPremiumFloor,
      config.minVolumeFloor,
    );
  }
  return false;
}

/**
 * Watches stable chain fetches and pushes configurable flow / liquidity alerts.
 */
export function useFlowLiquidityAlerts(enabled = true) {
  const lastFireRef = useRef(0);
  const inflightRef = useRef(false);

  const scanSymbol = useCallback(async (symbol: string) => {
    if (!symbol.trim() || inflightRef.current) return;
    const config = loadConfig();
    if (!config.notificationEnabled && !config.soundEnabled) return;
    if (!config.alertOnStableFetch) return;

    const now = Date.now();
    const cooldownMs = (config.cooldownMinutes || 0) * 60_000;
    if (cooldownMs > 0 && now - lastFireRef.current < cooldownMs) return;

    inflightRef.current = true;
    try {
      const sym = symbol.trim().toUpperCase();
      const res = await smartFlowService.getFlowOpportunities({
        symbol: sym,
        limit: 100,
        min_flow_score: 0,
      });
      if (res.serviceAvailable === false || !res.data?.length) return;

      const seen = readSeenIds();
      const hits: FlowOpportunity[] = [];

      for (const row of res.data) {
        if (row.symbol && row.symbol.toUpperCase() !== sym) continue;
        if (!matchesConfig(row, config)) continue;
        const key = rowDedupeKey(row);
        if (config.newOpportunitiesOnly && seen.has(key)) continue;
        hits.push(row);
        seen.add(key);
      }

      if (!hits.length) return;

      writeSeenIds(seen);
      lastFireRef.current = now;

      const top = [...hits].sort(
        (a, b) =>
          Number(b.premium_size ?? b.premium ?? 0) -
          Number(a.premium_size ?? a.premium ?? 0),
      )[0];

      const { title, body } = formatFlowLiquidityNotification(top);
      const multi =
        hits.length > 1
          ? `${body} (+${hits.length - 1} more on ${sym})`
          : body;

      if (config.soundEnabled) playSound(config.soundFile, config.volume);
      if (config.notificationEnabled) {
        pushBrowserNotification(title, multi);
      }

      window.dispatchEvent(
        new CustomEvent('mmec-flow-liquidity-alert', {
          detail: { symbol: sym, hits, title, body: multi },
        }),
      );
    } catch (e) {
      console.warn('[flow-liquidity-alerts]', e);
    } finally {
      inflightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onStableFetched = (e: Event) => {
      const ev = e as CustomEvent<{ symbols?: string[]; underlying?: string }>;
      const fromEvent =
        ev.detail?.symbols?.[0] || ev.detail?.underlying || '';
      const symbol = fromEvent || readStablePrimarySessionFirst();
      if (!symbol) return;
      window.setTimeout(() => {
        void scanSymbol(symbol);
      }, 2500);
    };

    window.addEventListener('stableDataFetched', onStableFetched);
    return () => window.removeEventListener('stableDataFetched', onStableFetched);
  }, [enabled, scanSymbol]);
}
